import { useCallback, useEffect } from "react";
import type { UseToastOptions } from "@chakra-ui/react";
import actions from "../../actions";
import type { DisplayFullOrganization } from "../../actions";
import { loadFbSdk } from "../EventDetail/Nav/Marketing/util";

type DialogFn = (opts: { text: string }) => void;

type UseFacebookMetaAdsConnectParams = {
  organization: DisplayFullOrganization | null;
  toast: (options: UseToastOptions) => void;
  dialog: DialogFn;
  onAccessGranted: () => Promise<void>;
  onLoadingChange?: (loading: boolean) => void;
};

const META_SCOPES = [
  "ads_management",
  "pages_read_engagement",
  "pages_show_list",
  "ads_read",
  "attribution_read",
  "instagram_basic",
  "pages_read_user_content",
  "pages_manage_ads",
  "instagram_branded_content_ads_brand",
  "facebook_branded_content_ads_brand",
  "business_management",
];

/**
 * Loads the Facebook JS SDK and exposes the same Meta Ads OAuth flow as Organization settings.
 */
export function useFacebookMetaAdsConnect({
  organization,
  toast,
  dialog,
  onAccessGranted,
  onLoadingChange,
}: UseFacebookMetaAdsConnectParams) {
  const sdkInit = useCallback(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (!w.FB || !organization) {
      return;
    }
    const APP_ID = organization.ads_manager?.app_id;
    w.FB.init({
      appId: APP_ID,
      cookie: true,
      xfbml: true,
      version: "v23.0",
    });
  }, [organization]);

  useEffect(() => {
    if (!organization) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).fbAsyncInit = sdkInit;
    loadFbSdk();
    // If the SDK is already on the page (e.g. user visited Settings first), init now.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (w.FB && typeof w.FB.init === "function") {
      sdkInit();
    }
  }, [sdkInit, organization]);

  const triggerFacebookLogin = useCallback(() => {
    onLoadingChange?.(true);
    if (!organization?.id) {
      toast({
        title: "Organization not loaded",
        description: "Please refresh the page and try again.",
        status: "error",
      });
      onLoadingChange?.(false);
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const w = window as any;
    if (!w.FB || typeof w.FB.login !== "function") {
      toast({
        title: "Meta login unavailable",
        description: "Open Organization settings to connect Meta Ads.",
        status: "info",
      });
      onLoadingChange?.(false);
      return;
    }

    w.FB.login(
      (response: {
        authResponse: { code: string; accessToken: string } | null;
      }) => {
        if (!response?.authResponse?.accessToken) {
          toast({
            title: "Login cancelled",
            description: "You can connect Meta ads anytime from Settings.",
            status: "info",
          });
          onLoadingChange?.(false);
          return;
        }

        actions.ADMIN.createOrganizationAccess({
          access_token: response.authResponse.accessToken,
        })
          .then(() => onAccessGranted())
          .then(() => {
            toast({
              title: "Meta ads connected",
              description:
                "KYD access to Meta ads has been enabled for your organization.",
              status: "success",
            });
          })
          .catch((err: { message?: string }) => {
            dialog({ text: err.message ?? "Something went wrong" });
          })
          .finally(() => onLoadingChange?.(false));
      },
      {
        config_id: organization.ads_manager?.config_id,
        scope: META_SCOPES.join(","),
      }
    );
  }, [organization, toast, dialog, onAccessGranted, onLoadingChange]);

  return { triggerFacebookLogin };
}
